import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { toast } from "../services/toast"

// Notification controller for toasts, badges, and sounds
// Works with ActionCable channels for real-time updates
export default class extends Controller {
  static targets = [
    "audio",
    "badge"
  ]

  static values = {
    userType: String,
    userId: String,
    notificationSound: { type: String, default: "Chord.ogg" },
    notificationsEnabled: { type: Boolean, default: true }
  }

  connect() {
    this.audioUnlocked = false
    this.pendingSound = false
    this.boundUnlockAudio = this.unlockAudio.bind(this)

    this.consumer = createConsumer()
    this.subscriptions = []
    this.setupSubscriptions()

    this.setupAudioUnlock()

    this.setupWebPush()
  }

  disconnect() {
    this.unsubscribeChannels()
    if (this.consumer) {
      this.consumer.disconnect()
    }

    this.teardownAudioUnlock()
  }

  async setupWebPush() {
    if (!this.notificationsEnabledValue) return
    if (!("serviceWorker" in navigator)) return
    if (!("PushManager" in window)) return

    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") return

      const registration = await navigator.serviceWorker.ready
      const existing = await registration.pushManager.getSubscription()
      const subscription = existing || (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: await this.fetchApplicationServerKey()
      }))

      await this.sendSubscriptionToServer(subscription)
    } catch (_e) {
      // Ignore failures (unsupported browser, denied, missing keys, etc.)
    }
  }

  async fetchApplicationServerKey() {
    const res = await fetch("/push/vapid_public_key", {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    const data = await res.json()
    return this.urlBase64ToUint8Array(data.public_key)
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i)
    return outputArray
  }

  async sendSubscriptionToServer(subscription) {
    const json = subscription.toJSON()
    const endpoint = json.endpoint

    const userId = this.userIdValue || "unknown"
    const cacheKey = `push-subscription:${userId}:${endpoint}`
    if (localStorage.getItem(cacheKey) === "1") return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    await fetch("/push_subscription", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { "X-CSRF-Token": token } : {})
      },
      body: JSON.stringify({
        subscription: {
          endpoint: json.endpoint,
          expiration_time: json.expirationTime,
          keys: {
            p256dh: json.keys?.p256dh,
            auth: json.keys?.auth
          }
        }
      })
    })

    localStorage.setItem(cacheKey, "1")
  }

  setupAudioUnlock() {
    if (!this.notificationsEnabledValue) return
    if (!this.hasAudioTarget) return

    // Browsers often block audio until a user gesture occurs.
    // We listen for the first gesture and unlock the audio element.
    document.addEventListener("pointerdown", this.boundUnlockAudio, { once: true })
    document.addEventListener("keydown", this.boundUnlockAudio, { once: true })
  }

  teardownAudioUnlock() {
    if (!this.boundUnlockAudio) return
    document.removeEventListener("pointerdown", this.boundUnlockAudio)
    document.removeEventListener("keydown", this.boundUnlockAudio)
  }

  unlockAudio() {
    if (!this.notificationsEnabledValue) return
    if (!this.hasAudioTarget) return
    if (this.audioUnlocked) return

    const audioElement = this.audioTarget

    try {
      // Ensure the configured source is loaded before unlocking.
      this.ensureAudioSource(audioElement)

      const previousMuted = audioElement.muted
      const previousVolume = audioElement.volume

      audioElement.muted = true
      audioElement.volume = 0
      audioElement.currentTime = 0

      const playPromise = audioElement.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            audioElement.pause()
            audioElement.currentTime = 0
            audioElement.muted = previousMuted
            audioElement.volume = previousVolume
            this.audioUnlocked = true

            if (this.pendingSound) {
              this.pendingSound = false
              this.playSound()
            }
          })
          .catch(() => {
            // Still locked; keep pending and wait for another gesture
            this.pendingSound = true
            this.setupAudioUnlock()
          })
      }
    } catch (_e) {
      this.pendingSound = true
      this.setupAudioUnlock()
    }
  }

  ensureAudioSource(audioElement) {
    const soundFile = this.notificationSoundValue
    const desiredSrc = `/sounds/${soundFile}`

    if (!audioElement.src || !audioElement.src.endsWith(desiredSrc)) {
      audioElement.src = desiredSrc
      audioElement.load()
    }
  }

  setupSubscriptions() {
    const userId = this.userIdValue
    const userType = this.userTypeValue

    if (!userId || !userType) return

    // Subscribe based on user type
    if (userType === "seller") {
      this.subscribeToChannel("WarehouseRequestChannel", "seller", userId, this.handleWarehouseRequestUpdate.bind(this))
      this.subscribeToChannel("SaleOrderChannel", "seller", userId, this.handleSaleOrderUpdate.bind(this))
    }

    if (userType === "worker") {
      this.subscribeToChannel("WarehouseRequestChannel", "worker", userId, this.handleWarehouseRequestUpdate.bind(this))
    }

    if (userType === "cashier") {
      this.subscribeToChannel("SaleOrderChannel", "cashier", userId, this.handleSaleOrderUpdate.bind(this))
    }
  }

  subscribeToChannel(channel, userType, userId, handler) {
    const subscription = this.consumer.subscriptions.create(
      { channel, user_type: userType, user_id: userId },
      {
        received: (data) => {
          handler(data)
        }
      }
    )
    this.subscriptions.push(subscription)
  }

  unsubscribeChannels() {
    if (this.subscriptions) {
      this.subscriptions.forEach(sub => sub.unsubscribe())
      this.subscriptions = []
    }
  }

  handleWarehouseRequestUpdate(data) {
    const userType = this.userTypeValue

    if (data.action === "new_request" && userType === "worker") {
      const priority = data.priority === "urgent" ? "🔴 URGENTE: " : ""
      toast.info(`${priority}${data.product_code} T${data.size}`)
      this.playSound()
      this.updateBadge("pending", data.pending_count)
    } else if (data.action === "status_changed" && userType === "seller") {
      // No alert needed when the request is completed (visual update comes from Turbo Streams)
      if (data.status === "completed") return

      const statusMessages = {
        in_progress: "Tu pedido está siendo atendido",
        rejected: "Tu pedido fue rechazado",
        cancelled: "Tu pedido fue cancelado"
      }

      const type = data.status === "rejected" || data.status === "cancelled" ? "error" : "info"

      const label = data.product_code ? `${data.product_code} T${data.size}` : null
      if (label) {
        toast.show(type, `${label}: ${statusMessages[data.status] || data.status}`)
      }

      document.dispatchEvent(new CustomEvent("warehouse-request:updated", {
        detail: {
          status: data.status,
          productCode: data.product_code,
          size: data.size
        }
      }))
      this.playSound()
    }
  }

  handleSaleOrderUpdate(data) {
    const userType = this.userTypeValue

    if (data.action === "new_order" && userType === "cashier") {
      toast.warning(`Nueva orden #${data.order_id} - Total: $${data.total}`)
      this.playSound()
      this.updateBadge("pending", data.pending_count)
    } else if (data.action === "status_changed" && userType === "seller") {
      const statusMessages = {
        processing: "Tu orden está siendo procesada",
        completed: "Tu orden ha sido completada",
        cancelled: "Tu orden fue cancelada"
      }

      const type = data.status === "completed" ? "success" : data.status === "cancelled" ? "error" : "info"
      toast.show(type, `Orden #${data.order_id}: ${statusMessages[data.status] || data.status}`)

      if (data.status === "completed") {
        this.playSound()
      }
    }
  }

  playSound() {
    // Check if notifications are enabled
    if (!this.notificationsEnabledValue) return
    if (!this.hasAudioTarget) return

    // If the browser hasn't unlocked audio yet, queue the sound.
    if (!this.audioUnlocked) {
      this.pendingSound = true
      this.setupAudioUnlock()
      return
    }

    try {
      const audioElement = this.audioTarget
      this.ensureAudioSource(audioElement)
      audioElement.currentTime = 0

      const playPromise = audioElement.play()

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Auto-play was prevented again; wait for next gesture
          this.audioUnlocked = false
          this.pendingSound = true
          this.setupAudioUnlock()
        })
      }
    } catch (error) {
      console.debug("Error playing notification sound:", error)
    }
  }

  updateBadge(badgeType, count) {
    // Find badges with the matching data-badge-type attribute
    this.badgeTargets.forEach(badge => {
      if (badge.dataset.badgeType === badgeType) {
        if (count > 0) {
          badge.textContent = count > 99 ? "99+" : `${count} pendientes`
          badge.style.display = ""
        } else {
          badge.style.display = "none"
        }
      }
    })
  }

  // Public method to show toast from other controllers
  notify({ type = "info", message }) {
    toast.show(type, message)
  }

  // Public method to update a specific badge
  setBadgeCount(badgeName, count) {
    this.updateBadge(badgeName, count)
  }
}
